/**
 * Hardcoded wearable-to-MAC address mapping configuration
 *
 * Each wearable has:
 * - id: Numeric identifier matching the physical label
 * - label: Display name (e.g., "Wearable 5")
 * - macAddress: The Bluetooth MAC address in format "AA:BB:CC:DD:EE:FF"
 *
 * TODO: Replace placeholder MAC addresses with actual values from your wearables
 */

import { t } from "i18n";

// `label` is a getter so it is translated at read time, not at module load.
const wearable = (id, macAddress) => ({
  id,
  get label() {
    return t("utils.wearables.label", { id });
  },
  macAddress,
});

/**
 * Master list of all wearables
 * Format MAC addresses as uppercase with colons (AA:BB:CC:DD:EE:FF)
 */
export const WEARABLES = [
  wearable(1, "34:85:18:04:18:42"),
  wearable(2, "34:85:18:06:31:B6"),
  wearable(3, "34:85:18:05:D2:7E"),
  wearable(4, "34:85:18:05:C3:2E"),
  wearable(5, "34:85:18:06:75:EE"),
  wearable(6, "34:85:18:05:0A:26"),
  wearable(7, "34:85:18:03:F7:82"),
  wearable(8, "34:85:18:06:2F:F6"),
  wearable(9, "34:85:18:06:2A:3E"),
  wearable(10, "34:85:18:03:64:2E"),
  wearable(11, "34:85:18:04:D0:42"),
  wearable(12, "AA:BB:CC:DD:EE:12"),
  wearable(13, "34:85:18:06:51:6A"),
  wearable(14, "34:85:18:07:E1:CA"),
  wearable(15, "34:85:18:05:B5:6A"),
  wearable(16, "34:85:18:07:51:56"),
  wearable(17, "34:85:18:04:C9:16"),
  wearable(18, "34:85:18:07:A3:BA"),
  wearable(19, "34:85:18:06:70:DA"),
  wearable(20, "34:85:18:06:3C:36"),
  // Add more wearables as needed
];

/**
 * Get a wearable by its ID
 */
export const getWearableById = (id) => {
  return WEARABLES.find((w) => w.id === id);
};

/**
 * Get a wearable by its MAC address
 */
export const getWearableByMac = (macAddress) => {
  // Normalize the MAC address for comparison (uppercase, with colons)
  const normalizedMac = macAddress.toUpperCase().replace(/[:-]/g, "");
  return WEARABLES.find((w) => {
    const wearableMac = w.macAddress.toUpperCase().replace(/[:-]/g, "");
    return wearableMac === normalizedMac;
  });
};

/**
 * Filter wearables by search query (matches ID or label)
 */
export const filterWearables = (query) => {
  if (!query.trim()) {
    return WEARABLES;
  }

  const lowerQuery = query.toLowerCase();
  return WEARABLES.filter((w) => {
    return (
      w.id.toString().includes(lowerQuery) ||
      w.label.toLowerCase().includes(lowerQuery) ||
      w.macAddress.toLowerCase().includes(lowerQuery)
    );
  });
};

/**
 * Get wearables that are NOT in the provided list of MAC addresses
 */
export const getAvailableWearables = (assignedMacAddresses) => {
  const normalizedAssigned = assignedMacAddresses.map((mac) =>
    mac.toUpperCase().replace(/[:-]/g, "")
  );

  return WEARABLES.filter((w) => {
    const wearableMac = w.macAddress.toUpperCase().replace(/[:-]/g, "");
    return !normalizedAssigned.includes(wearableMac);
  });
};

/**
 * Check if a wearable is already assigned
 */
export const isWearableAssigned = (macAddress, assignedMacAddresses) => {
  const normalizedMac = macAddress.toUpperCase().replace(/[:-]/g, "");
  const normalizedAssigned = assignedMacAddresses.map((mac) =>
    mac.toUpperCase().replace(/[:-]/g, "")
  );
  return normalizedAssigned.includes(normalizedMac);
};
